import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import AnimatedSection from '../components/common/AnimatedSection';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Target, Heart, Users, MapPin, Briefcase } from 'lucide-react';

const AboutPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();

  // Content from PDF - ADA Presentation
  const content = {
    fr: {
      title: "ADA",
      subtitle: "Alliance Démocratique Africaine",
      localTag: "Parti politique • Côte d'Ivoire",
      officialBadge: "Parti autorisé depuis septembre 2024",
      presentation: {
        title: "De plateforme de réflexion à parti politique",
        paragraphs: [
          "Créée en 2012 comme plateforme de réflexion politique et économique, l'Alliance Démocratique Africaine (ADA) est devenue un parti politique officiellement autorisé en septembre 2024. Notre mission : apporter des réponses concrètes aux défis qui affectent les Ivoiriens.",
          "Forte de 2000 contributeurs, l'ADA est particulièrement active en zone rurale et travaille aux côtés des jeunes et des communautés locales. Nos priorités : l'accès à l'éducation et aux infrastructures, l'égalité des chances, et le développement de l'entrepreneuriat local.",
          "D'inspiration libérale, l'ADA défend les valeurs de responsabilité collective et individuelle, d'éthique républicaine, de liberté, de primauté du droit et des institutions, et de tolérance. Nous encourageons l'autonomie politique et économique des citoyens."
        ]
      },
      origins: {
        title: "Aux origines",
        text: "À l'origine de ce mouvement se trouve Alpha Diallo, entrepreneur ivoirien engagé dans le développement de son pays. Fondateur de l'association \"Demain t'appartient\", il œuvre depuis des années dans les secteurs stratégiques pour l'avenir de la Côte d'Ivoire."
      },
      contributors: {
        title: "Nos militants et contributeurs",
        scrollingPhrases: [
          "Nous venons d'ici et d'ailleurs — de toute la Côte d'Ivoire.",
          "Tiassalé, Yopougon, Adjamé, Bouaké, Séguéla, Gagnoa, Divo…",
          "Nous sommes les villages qui dialoguent avec les villes."
        ],
        spirit: "Parti composé d'hommes et de femmes de terrain, nous tirons des leçons du passé pour proposer des solutions concrètes aux défis d'aujourd'hui.",
        diversity: "Modestes ou un peu plus aisés, chrétiens ou musulmans, citadins ou ruraux, sans diplômes ou très diplômés — chacun et chacune a droit à la parole et le devoir de proposer une réponse possible.",
        profiles: "Planteurs, fonctionnaires ruraux, créateurs d'entreprise, petits industriels, professeurs, cadres, étudiants… voilà l'autre visage de l'ADA."
      },
      values: {
        title: "Nos valeurs",
        items: [
          { icon: Heart, title: "Responsabilité", description: "Collective et individuelle. Chaque action engage notre nom et notre parole." },
          { icon: Target, title: "Éthique républicaine", description: "Primauté du droit et des institutions. La confiance se mérite." },
          { icon: Globe, title: "Liberté & Tolérance", description: "Autonomie politique et économique des citoyens dans le respect de tous." }
        ]
      }
    },
    en: {
      title: "ADA",
      subtitle: "African Democratic Alliance",
      localTag: "Political Party • Côte d'Ivoire",
      officialBadge: "Authorized party since September 2024",
      presentation: {
        title: "From think tank to political party",
        paragraphs: [
          "Founded in 2012 as a political and economic think tank, the Ivorian Democratic Alliance (ADA) became an officially authorized political party in September 2024. Our mission: to provide concrete answers to the challenges affecting Ivorians.",
          "With 2,000 contributors, ADA is particularly active in rural areas and works alongside young people and local communities. Our priorities: access to education and infrastructure, equal opportunities, and developing local entrepreneurship.",
          "With liberal inspiration, ADA defends the values of collective and individual responsibility, republican ethics, freedom, the primacy of law and institutions, and tolerance. We encourage the political and economic autonomy of citizens."
        ]
      },
      origins: {
        title: "Our Origins",
        text: "At the origin of this movement is Alpha Diallo, an Ivorian entrepreneur committed to his country's development. Founder of the \"Tomorrow Belongs to You\" association, he has been working for years in strategic sectors for Côte d'Ivoire's future."
      },
      contributors: {
        title: "Our Activists and Contributors",
        scrollingPhrases: [
          "We come from here and elsewhere — from all over Côte d'Ivoire.",
          "Tiassalé, Yopougon, Adjamé, Bouaké, Séguéla, Gagnoa, Divo…",
          "We are the villages that dialogue with the cities."
        ],
        spirit: "A party of men and women from the field, we draw lessons from the past to propose concrete solutions to today's challenges.",
        diversity: "Modest or better off, Christian or Muslim, urban or rural, with or without diplomas — everyone has the right to speak and the duty to propose a possible answer.",
        profiles: "Planters, rural civil servants, business creators, small industrialists, teachers, executives, students… this is the other face of ADA."
      },
      values: {
        title: "Our Values",
        items: [
          { icon: Heart, title: "Responsibility", description: "Collective and individual. Every action commits our name and word." },
          { icon: Target, title: "Republican Ethics", description: "Primacy of law and institutions. Trust must be earned." },
          { icon: Globe, title: "Freedom & Tolerance", description: "Political and economic autonomy of citizens with respect for all." }
        ]
      }
    }
  };

  const c = content[language] || content.fr;

  // Cities for the scrolling display
  const cities = ["Tiassalé", "Yopougon", "Adjamé", "Bouaké", "Séguéla", "Gagnoa", "Divo", "Korhogo", "Man", "San-Pédro", "Bondoukou", "Daloa"];

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-[#9B6FCE] via-[#7B4AAE] to-[#5B2D8E] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#F7C948]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#0D9488]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection delay={0.1}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                {c.title}
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
                {c.subtitle}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7C948] rounded-full text-[#0A0510] text-sm font-bold">
                  <MapPin className="w-4 h-4" />
                  {c.localTag}
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-bold">
                  🏛️ {c.officialBadge}
                </span>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Presentation */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">{c.presentation.title}</span>
              <div className="divider mb-10" />
            </AnimatedSection>

            <div className="space-y-8">
              {c.presentation.paragraphs.map((paragraph, index) => (
                <AnimatedSection key={index} delay={index * 0.15}>
                  <p className="text-lg lg:text-xl leading-relaxed text-theme-secondary" style={{ fontFamily: 'var(--font-serif)' }}>
                    {paragraph}
                  </p>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Origins */}
      <section className={`section ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">{c.origins.title}</span>
              <div className="divider mb-10" />
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className={`rounded-2xl p-8 border ${isDark ? 'bg-[#292524] border-white/10' : 'bg-white border-[#E8E0D4]'}`}>
                <p className="text-lg lg:text-xl leading-relaxed text-theme-secondary" style={{ fontFamily: 'var(--font-serif)' }}>
                  {c.origins.text}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contributors */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">{c.contributors.title}</span>
              <div className="divider mb-10" />
            </AnimatedSection>

            {/* Scrolling phrases */}
            <div className="mb-12 overflow-hidden">
              <div className="space-y-4">
                {c.contributors.scrollingPhrases.map((phrase, index) => (
                  <AnimatedSection key={index} delay={index * 0.15}>
                    <p className={`text-xl lg:text-2xl font-medium ${
                      index === 1 ? 'text-[#FFD980]' : isDark ? 'text-[#7C4DAF]' : 'text-[#7B4AAE]'
                    }`} style={{ fontFamily: 'var(--font-serif)' }}>
                      {phrase}
                    </p>
                  </AnimatedSection>
                ))}
              </div>
            </div>

            {/* Cities ticker */}
            <AnimatedSection delay={0.4}>
              <div className={`rounded-xl p-4 mb-10 overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
                <motion.div 
                  className={`flex gap-6 ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}
                  animate={{ x: [0, -500] }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                >
                  {[...cities, ...cities].map((city, index) => (
                    <span key={index} className="flex items-center gap-2 whitespace-nowrap">
                      <MapPin className="w-4 h-4 text-[#FFD980]" />
                      {city}
                    </span>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Spirit and diversity */}
            <div className="space-y-6">
              <AnimatedSection delay={0.5}>
                <p className="text-lg leading-relaxed text-theme-secondary" style={{ fontFamily: 'var(--font-serif)' }}>
                  {c.contributors.spirit}
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.6}>
                <div className={`rounded-xl p-6 border-l-4 border-[#7B4AAE] ${isDark ? 'bg-[#7B4AAE]/10' : 'bg-gradient-to-r from-[#7B4AAE]/5 to-[#FFD980]/5'}`}>
                  <p className="text-lg leading-relaxed text-theme-secondary" style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
                    {c.contributors.diversity}
                  </p>
                </div>
              </AnimatedSection>
              <AnimatedSection delay={0.7}>
                <p className={`text-lg leading-relaxed font-medium ${isDark ? 'text-[#7C4DAF]' : 'text-[#7B4AAE]'}`} style={{ fontFamily: 'var(--font-serif)' }}>
                  {c.contributors.profiles}
                </p>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className={`section ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-16">
            <span className="caption mb-4 block">{c.values.title}</span>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {c.values.items.map((value, index) => {
              const Icon = value.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.15}>
                  <motion.div 
                    className={`rounded-2xl h-full text-center p-8 border ${isDark ? 'bg-[#292524] border-white/10' : 'bg-white border-[#E8E0D4]'}`}
                    whileHover={{ y: -8 }}
                  >
                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#7B4AAE] to-[#9B6FCE] rounded-2xl flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-theme-primary mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                      {value.title}
                    </h3>
                    <p className="text-theme-secondary">{value.description}</p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ambition Quote */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">{language === 'fr' ? 'Notre ambition' : 'Our ambition'}</span>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <blockquote className="text-2xl lg:text-3xl text-theme-primary leading-relaxed" style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
                « {language === 'fr' 
                  ? 'Ce mouvement sait où il va. Il prend le temps long. Il parle à l\'Histoire.' 
                  : 'This movement knows where it\'s going. It takes the long view. It speaks to History.'} »
              </blockquote>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#7B4AAE] to-[#3D1E5F]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
                {language === 'fr' ? 'Faites partie de l\'histoire' : 'Be part of history'}
              </h2>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <p className="text-xl text-white/80 mb-10" style={{ fontFamily: 'var(--font-serif)' }}>
                {language === 'fr' 
                  ? 'L\'ADA vous invite à contribuer à la refondation de la Nation.'
                  : 'ADA invites you to contribute to the refounding of the Nation.'}
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/rejoindre" 
                  className="btn-secondary"
                >
                  {language === 'fr' ? 'Rejoindre l\'ADA' : 'Join ADA'}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/notre-posture" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  {language === 'fr' ? 'Notre posture' : 'Our Posture'}
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
